import { Injectable, inject } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { HttpService } from './http.service';
import { ApiService } from './api.service';
import * as _ from 'lodash';
import { Ad, AdMetrics, Creative, CreativeMetrics } from '../interfaces/performance-insights';

@Injectable({
  providedIn: 'root'
})
export class PerformanceInsightsService {
    // Dependency Injection
    private apiService: ApiService = inject(ApiService);
    private httpService: HttpService = inject(HttpService);

    constructor() { 
        console.log("[PerformanceInsightsService] Initialising...");
    }


    // Get ads
    async getAds(acc_id: string, options: { limit?: number, date_start: string, date_end: string, order_by?: string, order_dir?: string, product_code?: string, ad_name_contains?: string, ad_name_contains_2?: string }) {
        return new Promise<Ad[]>((resolve, reject) => {
            console.log("[PerformanceInsightsService] getAds()", acc_id, options);
            let requestPath = `/performance-insights/ads?acc_id=${acc_id}`;
            if(options['date_start']) {
                requestPath += `&date_start=${options['date_start']}`;
            }
            if(options['date_end']) {
                requestPath += `&date_end=${options['date_end']}`;
            }
            if(options['limit']) {
                requestPath += `&limit=${options['limit']}`;
            }
            if(options['order_by']) {
                requestPath += `&order_by=${options['order_by']}`;
            }
            if(options['order_dir']) {
                requestPath += `&order_dir=${options['order_dir']}`;
            }
            if(options['product_code']) {
                requestPath += `&product=${options['product_code']}`;
            }
            if(options['ad_name_contains']) {
                requestPath += `&ad_name_contains=${options['ad_name_contains']}`;
            }
            if(options['ad_name_contains_2']) {
                requestPath += `&ad_name_contains_2=${options['ad_name_contains_2']}`;
            }
            let requestParams = { params:
                new HttpParams()
            };
            this.apiService.get(requestPath, requestParams)
            .then(response => {
                console.log("[PerformanceInsightsService] getAds() API response", response);

                // Get ads from response
                let ads: Ad[] = [];
                if (response.data && response.data.results && response.data.results.length > 0) {
                    ads = response.data.results.map((ad: any) => {
                        return {
                            id: ad.id,
                            name: ad.name,
                            adset_id: ad.adset_id,
                            adset_name: ad.adset_name,
                            campaign_id: ad.campaign_id,
                            campaign_name: ad.campaign_name,
                            creative_image_url: ad.creative_image_url,
                            metrics: {
                                amount_spent: ad.amount_spent,
                                purchases: ad.purchases,
                                revenue: ad.revenue,
                                roas: ad.roas,
                            }
                        };
                    });
                } else {
                    console.log("[PerformanceInsightsService] getAds() No ads found");
                }           
                console.log("[PerformanceInsightsService] getAds() ad count", ads.length);    
                resolve(ads);
            })
            .catch(error => {
                console.log("[PerformanceInsightsService] getAds() API error", error);
                reject(error.name);
            });
        });
    }


    // Get ads per day
    async getAdsPerDay(acc_id: string, options: { date_start: string, date_end: string, order_by?: string, order_dir?: string }) {
        return new Promise<{date: string, metrics: AdMetrics}[]>((resolve, reject) => {
            console.log("[PerformanceInsightsService] getAdsPerDay()", acc_id, options);
            let requestPath = `/performance-insights/ads-per-day?acc_id=${acc_id}`;
            if(options['date_start']) {
                requestPath += `&date_start=${options['date_start']}`;
            }
            if(options['date_end']) {
                requestPath += `&date_end=${options['date_end']}`;
            }
            if(options['order_by']) {
                requestPath += `&order_by=${options['order_by']}`;
            }
            if(options['order_dir']) {
                requestPath += `&order_dir=${options['order_dir']}`;
            }
            let requestParams = { params:
                new HttpParams()
            };
            this.apiService.get(requestPath, requestParams)
            .then(response => {
                console.log("[PerformanceInsightsService] getAdsPerDay() API response", response);

                // Get ads from response
                let adsPerDay: {date: string, metrics: AdMetrics}[] = [];
                if (response.data && response.data.results && response.data.results.length > 0) {
                    adsPerDay = response.data.results.map((day: any) => {
                        return {
                            date: day.date,
                            metrics: {
                                amount_spent: day.amount_spent,
                                purchases: day.purchases,
                                revenue: day.revenue,
                                roas: day.roas,
                            }
                        };
                    });
                } else {
                    console.log("[PerformanceInsightsService] getAdsPerDay() No ads found");
                }           
                console.log("[PerformanceInsightsService] getAdsPerDay() ad count", adsPerDay.length);    
                resolve(adsPerDay);
            })
            .catch(error => {
                console.log("[PerformanceInsightsService] getAdsPerDay() API error", error);
                reject(error.name);
            });
        });
    }


    // Get creatives
    async getCreatives(acc_id: string, options: { limit?: number, date_start: string, date_end: string, amount_spent_min?: number, amount_spent_max?: number, order_by?: string, order_dir?: string, creative_source?: string }) {
        return new Promise<Creative[]>((resolve, reject) => {
            console.log("[PerformanceInsightsService] getCreatives()", acc_id, options);
            let requestPath = `/performance-insights/creatives?acc_id=${acc_id}`;
            if(options['date_start']) {
                requestPath += `&date_start=${options['date_start']}`;
            }
            if(options['date_end']) {
                requestPath += `&date_end=${options['date_end']}`;
            }
            if(options['limit']) {
                requestPath += `&limit=${options['limit']}`;
            }
            if(options['order_by']) {
                requestPath += `&order_by=${options['order_by']}`;
            }
            if(options['order_dir']) {
                requestPath += `&order_dir=${options['order_dir']}`;
            }
            if(options['creative_source']) {
                requestPath += `&creative_source=${options['creative_source']}`;
            }
            if(options['amount_spent_min']) {
                requestPath += `&amount_spent_min=${options['amount_spent_min']}`;
            }
            if(options['amount_spent_max']) {
                requestPath += `&amount_spent_max=${options['amount_spent_max']}`;
            }            let requestParams = { params:
                new HttpParams()
            };
            this.apiService.get(requestPath, requestParams)
            .then(response => {
                console.log("[PerformanceInsightsService] getCreatives() API response", response);

                // Get creatives from response
                let creatives: Creative[] = [];
                if (response.data && response.data.results && response.data.results.length > 0) {
                    creatives = response.data.results.map((creative: any) => {
                        return {
                            id: creative.id,
                            type: creative.type,
                            image_url: creative.image_url,
                            creative_source: creative.creative_source,
                            creator: creative.creator,
                            metrics: {
                                amount_spent: creative.amount_spent,
                                purchases: creative.purchases,
                                revenue: creative.revenue,
                                roas: creative.roas,
                            }
                        };
                    });
                } else {
                    console.log("[PerformanceInsightsService] getCreatives() No creatives found");
                }           
                console.log("[PerformanceInsightsService] getCreatives() creative count", creatives.length);    
                resolve(creatives);
            })
            .catch(error => {
                console.log("[PerformanceInsightsService] getCreatives() API error", error);
                reject(error.name);
            });
        });
    }    

}